import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Menu from "@mui/material/Menu";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DownloadIcon from "@mui/icons-material/Download";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import {
  Alert,
  Box,
  Paper,
  Toolbar,
  Typography,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Snackbar,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import axios from "axios";
import ActionsMenu from "./ActionsMenu";
import MenuItem from "@mui/material/MenuItem";
import Swal from "sweetalert2";

function BookActionsMenu({ onAdd, onDownload, onUpload }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  return (
    <>
      <IconButton
        color="primary"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          backgroundColor: "#1976d2",
          color: "#fff",
          ml: 1,
          "&:hover": { backgroundColor: "#115293" },
        }}
      >
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)}>
        <MenuItem disableRipple>
          <Button
            fullWidth
            variant="contained"
            startIcon={<AddIcon />}
            sx={{
              backgroundColor: "#1976d2",
              color: "#fff",
              textTransform: "none",
              "&:hover": { backgroundColor: "#115293" },
            }}
            onClick={() => {
              setAnchorEl(null);
              onAdd();
            }}
          >
            Add Book
          </Button>
        </MenuItem>
        <MenuItem disableRipple>
          <Button
            fullWidth
            variant="contained"
            startIcon={<DownloadIcon />}
            sx={{
              backgroundColor: "#4caf50",
              color: "#fff",
              textTransform: "none",
              "&:hover": { backgroundColor: "#357a38" },
            }}
            onClick={() => {
              setAnchorEl(null);
              onDownload();
            }}
          >
            Download CSV
          </Button>
        </MenuItem>
        <MenuItem disableRipple>
          <Button
            fullWidth
            variant="contained"
            component="label"
            startIcon={<UploadFileIcon />}
            sx={{
              backgroundColor: "#ff9800",
              color: "#fff",
              textTransform: "none",
              "&:hover": { backgroundColor: "#c77700" },
            }}
          >
            Upload CSV
            <input
              hidden
              type="file"
              accept=".csv"
              onChange={(e) => {
                setAnchorEl(null);
                onUpload(e);
              }}
            />
          </Button>
        </MenuItem>
      </Menu>
    </>
  );
}

BookActionsMenu.propTypes = {
  onAdd: PropTypes.func.isRequired,
  onDownload: PropTypes.func.isRequired,
  onUpload: PropTypes.func.isRequired,
};

export default function Book() {
  // Get IDs and user info
  const user = JSON.parse(sessionStorage.getItem("user") || "{}");
  const clientId = user?.client_id;
  const collegeId = user?.college_id;
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  // ALL STATE HOOKS MUST BE DECLARED FIRST
  const [idError, setIdError] = React.useState("");

  // Main state
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    category_id: "",
    publisher_id: "",
    title: "",
    edition: "",
    isbn: "",
    publication_year: "",
    language: "",
    delete_status: 0,
  });
  // Pagination state
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [rowCount, setRowCount] = useState(0);
  const [categories, setCategories] = useState([]);
  const [publishers, setPublishers] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // State for Add Book Copy dialog
  const [addCopyOpen, setAddCopyOpen] = useState(false);
  const [addCopyBook, setAddCopyBook] = useState(null);
  const [addCopyForm, setAddCopyForm] = useState({
    accession_no: "",
    collection_type_id: "",
    vendor_id: "",
    price: "",
    purchase_date: "",
    shelf_location: "",
    book_status: "AVAILABLE",
  });
  const [collectionTypes, setCollectionTypes] = useState([]);
  const [vendors, setVendors] = useState([]);

  // State for Edit Book Copy dialog
  const [editCopyOpen, setEditCopyOpen] = useState(false);
  const [editCopyForm, setEditCopyForm] = useState({});
  const [editCopyId, setEditCopyId] = useState(null);

  // State for View Book Copies dialog
  const [viewCopiesOpen, setViewCopiesOpen] = useState(false);
  const [viewCopiesBook, setViewCopiesBook] = useState(null);
  const [bookCopies, setBookCopies] = useState([]);
  const [loadingCopies, setLoadingCopies] = useState(false);

  // ALL EFFECT HOOKS MUST BE DECLARED NEXT
  // ID error check effect
  useEffect(() => {
    if (!clientId || !collegeId || clientId === "null" || collegeId === "null") {
      setIdError("Missing or invalid client/college ID. Please log in again or contact admin.");
    } else {
      setIdError("");
    }
  }, [clientId, collegeId]);

  // Ensure SweetAlert is always on top of MUI Dialogs
  useEffect(() => {
    const swalStyle = document.createElement("style");
    swalStyle.innerHTML = `.swal2-z-topmost { z-index: 9999 !important; } .swal2-container { z-index: 9999 !important; }`;
    swalStyle.setAttribute("data-swal-zindex", "true");
    if (!document.head.querySelector("[data-swal-zindex]")) {
      document.head.appendChild(swalStyle);
    }
    return () => {
      if (swalStyle.parentNode) swalStyle.parentNode.removeChild(swalStyle);
    };
  }, []);

  // Fetch initial data
  useEffect(() => {
    if (clientId && collegeId && clientId !== "null" && collegeId !== "null") {
      fetchData();
      fetchCategories();
      fetchPublishers();
    }
  }, [clientId, collegeId]);

  // Fetch collection types and vendors when needed
  const fetchCollectionTypesAndVendors = async () => {
    try {
      const [typesRes, vendorsRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/book/collection-types/${clientId}/${collegeId}`),
        axios.get(`${API_BASE_URL}/api/book/vendors/${clientId}/${collegeId}`),
      ]);
      setCollectionTypes(
        typesRes.data.success && Array.isArray(typesRes.data.data) ? typesRes.data.data : []
      );
      setVendors(
        vendorsRes.data.success && Array.isArray(vendorsRes.data.data) ? vendorsRes.data.data : []
      );
    } catch (err) {
      console.error("Error fetching collection types or vendors:", err);
      setCollectionTypes([]);
      setVendors([]);
    }
  };

  // Fetch categories
  const fetchCategories = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/book/${clientId}/${collegeId}/categories`
      );
      const categoryData =
        response.data.success && Array.isArray(response.data.data) ? response.data.data : [];
      setCategories(categoryData);
    } catch (err) {
      console.error("Error fetching categories:", err);
      setCategories([]);
    }
  };

  // Fetch publishers
  const fetchPublishers = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/book/${clientId}/${collegeId}/publishers`
      );
      const publisherData =
        response.data.success && Array.isArray(response.data.data) ? response.data.data : [];
      setPublishers(publisherData);
    } catch (err) {
      console.error("Error fetching publishers:", err);
      setPublishers([]);
    }
  };

  // Fetch books data (server-side pagination)
  const fetchData = async (page = 0, pageSize = 10, search = "") => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/book/${clientId}/${collegeId}?page=${
          page + 1
        }&pageSize=${pageSize}&search=${encodeURIComponent(search)}`
      );
      const items =
        response.data.success && response.data.data && Array.isArray(response.data.data.items)
          ? response.data.data.items
              .filter((row) => row.delete_status === 0)
              .map((row) => ({ ...row, id: row.book_id }))
          : [];
      setRows(items);
      setRowCount(response.data.data.total || 0);
    } catch (err) {
      console.error("Error fetching books:", err);
      setSnackbar({
        open: true,
        message: "Failed to fetch books",
        severity: "error",
      });
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  // Handler for downloading all Book data as CSV (delete_status = 0)
  const handleDownloadCsv = async () => {
    try {
      // Fetch all books with delete_status 0
      const response = await axios.get(`${API_BASE_URL}/api/book/${clientId}/${collegeId}`);
      const items =
        response.data.success && response.data.data && Array.isArray(response.data.data.items)
          ? response.data.data.items.filter((row) => row.delete_status === 0)
          : [];
      if (!items.length) {
        alert("No book data to export.");
        return;
      }
      // Prepare CSV header and rows (include book_id for update support)
      const headers = [
        "book_id",
        "Title",
        "Edition",
        "ISBN",
        "Publication Year",
        "Language",
        "Category",
        "Publisher",
      ];
      const csvRows = [headers.join(",")];
      items.forEach((row) => {
        csvRows.push(
          [
            row.book_id || "",
            `"${row.title || ""}"`,
            `"${row.edition || ""}"`,
            `"${row.isbn || ""}"`,
            `"${row.publication_year || ""}"`,
            `"${row.language || ""}"`,
            `"${row.category?.category_name || ""}"`,
            `"${row.publisher?.publisher_name || ""}"`,
          ].join(",")
        );
      });
      const csvContent = csvRows.join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "books.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      alert("Failed to download book data as CSV.");
    }
  };

  // Handler for uploading Book CSV
  const handleUploadCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      const { data } = await axios.post(
        `${API_BASE_URL}/api/book/${clientId}/${collegeId}/upload/csv`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      setSnackbar({
        open: true,
        message: data.message || "CSV uploaded successfully",
        severity: "success",
      });
      fetchData();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || "Upload failed",
        severity: "error",
      });
    } finally {
      e.target.value = ""; // reset file input
    }
  };

  // Handler to open Add Book Copy dialog
  const handleAddBookCopy = (row) => {
    setAddCopyBook(row);
    setAddCopyForm({
      accession_no: "",
      collection_type_id: "",
      vendor_id: "",
      price: "",
      purchase_date: "",
      shelf_location: "",
      book_status: "AVAILABLE",
    });
    setAddCopyOpen(true);
    fetchCollectionTypesAndVendors();
  };

  const handleAddCopyClose = () => setAddCopyOpen(false);

  const handleAddCopyChange = (e) => {
    setAddCopyForm({
      ...addCopyForm,
      [e.target.name]: e.target.value,
    });
  };

  const handleAddCopySave = async () => {
    try {
      await axios.post(`${API_BASE_URL}/api/book/copy/${clientId}/${collegeId}`, {
        ...addCopyForm,
        book_id: addCopyBook.book_id,
        created_by: user?.user_email || "system",
      });
      setSnackbar({ open: true, message: "Book copy added successfully", severity: "success" });
      handleAddCopyClose();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || "Failed to add book copy",
        severity: "error",
      });
    }
  };

  // Handlers for Book Copy actions
  const handleEditBookCopy = (copy) => {
    let dateStr = "";
    if (copy.purchase_date) {
      if (typeof copy.purchase_date === "string") {
        dateStr = copy.purchase_date.split("T")[0];
      } else if (copy.purchase_date instanceof Date) {
        dateStr = copy.purchase_date.toISOString().split("T")[0];
      }
    }
    fetchCollectionTypesAndVendors();
    setEditCopyForm({
      ...copy,
      purchase_date: dateStr,
      collection_type_id:
        copy.collection_type_id !== undefined
          ? copy.collection_type_id
          : copy.collection_type
          ? copy.collection_type.collection_type_id
          : "",
      vendor_id:
        copy.vendor_id !== undefined ? copy.vendor_id : copy.vendor ? copy.vendor.vendor_id : "",
    });
    setEditCopyId(copy.copy_id);
    setEditCopyOpen(true);
  };

  const handleEditCopyChange = (e) => {
    setEditCopyForm({
      ...editCopyForm,
      [e.target.name]: e.target.value,
    });
  };

  const handleEditCopySave = async () => {
    try {
      await axios.put(`${API_BASE_URL}/api/book/copy/${clientId}/${collegeId}/${editCopyId}`, {
        ...editCopyForm,
        collection_type_id:
          editCopyForm.collection_type_id === "" ? "" : Number(editCopyForm.collection_type_id),
        vendor_id: editCopyForm.vendor_id === "" ? "" : Number(editCopyForm.vendor_id),
        book_id: viewCopiesBook?.book_id ? Number(viewCopiesBook.book_id) : undefined,
        updated_by: user?.user_email || "system",
      });
      setSnackbar({ open: true, message: "Book copy updated successfully", severity: "success" });
      setEditCopyOpen(false);
      // Refresh book copies
      if (viewCopiesBook) {
        handleViewBookCopies(viewCopiesBook);
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || "Failed to update book copy",
        severity: "error",
      });
    }
  };

  const handleDeleteBookCopy = async (copy) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This will delete the book copy.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, keep it!",
      customClass: {
        popup: "swal2-z-topmost",
      },
    });
    if (!result.isConfirmed) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/book/copy/${clientId}/${collegeId}/${copy.copy_id}`);
      setSnackbar({ open: true, message: "Book copy deleted successfully", severity: "success" });
      // Refresh book copies
      if (viewCopiesBook) {
        handleViewBookCopies(viewCopiesBook);
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || "Failed to delete book copy",
        severity: "error",
      });
    }
  };

  const handleOpen = () => {
    setEditing(null);
    setForm({
      category_id: "",
      publisher_id: "",
      title: "",
      edition: "",
      isbn: "",
      publication_year: "",
      language: "",
      delete_status: 0,
    });
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleEdit = (row) => {
    setEditing(row);
    setForm({
      category_id: row.category_id || "",
      publisher_id: row.publisher_id || "",
      title: row.title,
      edition: row.edition || "",
      isbn: row.isbn || "",
      publication_year: row.publication_year || "",
      language: row.language || "",
      delete_status: row.delete_status,
    });
    setOpen(true);
  };

  const handleDelete = async (row) => {
    const result = await Swal.fire({
      title: "Are you sure? 🧸",
      text: "This will hide the book, but you can always add a new one!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it! 💖",
      cancelButtonText: "No, keep it! 🥺",
    });
    if (!result.isConfirmed) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/book/${clientId}/${collegeId}/${row.book_id}`);
      await Swal.fire("Deleted! 🗑️", "Your book has been deleted (softly)!", "success");
      fetchData();
    } catch (err) {
      await Swal.fire(
        "SORRY! 😢",
        err?.response?.data?.message || "Delete failed. Please try again.",
        "error"
      );
    }
  };

  const handleSubmit = async () => {
    try {
      if (editing) {
        await axios.put(`${API_BASE_URL}/api/book/${clientId}/${collegeId}/${editing.book_id}`, {
          ...form,
          updated_by: user?.user_email || "system",
          updated_on: new Date().toISOString(),
        });
        setSnackbar({ open: true, message: "Updated successfully", severity: "success" });
      } else {
        await axios.post(`${API_BASE_URL}/api/book/${clientId}/${collegeId}`, {
          ...form,
          college_id: collegeId,
          created_by: user?.user_email || "system",
        });
        setSnackbar({ open: true, message: "Created successfully", severity: "success" });
      }
      setOpen(false);
      fetchData();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || "Save failed",
        severity: "error",
      });
    }
  };

  // Handler to open View Book Copies dialog
  const handleViewBookCopies = async (row) => {
    setViewCopiesBook(row);
    setViewCopiesOpen(true);
    setLoadingCopies(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/book/copy/${clientId}/${collegeId}/${row.book_id}`
      );
      const copiesData =
        response.data.success && Array.isArray(response.data.data) ? response.data.data : [];
      setBookCopies(copiesData);
    } catch (err) {
      console.error("Error fetching book copies:", err);
      setBookCopies([]);
    } finally {
      setLoadingCopies(false);
    }
  };

  // Safely filter rows - ensure rows is an array
  const filteredRows = Array.isArray(rows)
    ? rows.filter(
        (row) => row.delete_status === 0 && row.title?.toLowerCase().includes(search.toLowerCase())
      )
    : [];

  const columns = [
    {
      field: "serial",
      headerName: "S. No.",
      width: 90,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        return params.api.getRowIndex(params.id) + 1;
      },
    },
    { field: "title", headerName: "Title", flex: 1 },
    { field: "edition", headerName: "Edition", flex: 1 },
    { field: "isbn", headerName: "ISBN", flex: 1 },
    { field: "publication_year", headerName: "Publication Year", flex: 1 },
    { field: "language", headerName: "Language", flex: 1 },
    {
      field: "category_id",
      headerName: "Category",
      flex: 1,
      valueGetter: (params) => params.row.category?.category_name || "",
    },
    {
      field: "publisher_id",
      headerName: "Publisher",
      flex: 1,
      valueGetter: (params) => params.row.publisher?.publisher_name || "",
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.5,
      sortable: false,
      renderCell: (params) => (
        <ActionsMenu
          row={params.row}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onAddBookCopy={handleAddBookCopy}
          onViewBookCopies={handleViewBookCopies}
        />
      ),
    },
  ];

  // Define columns for book copies
  const bookCopiesColumns = [
    { field: "copy_id", headerName: "Copy ID", width: 100 },
    { field: "accession_no", headerName: "Accession No", width: 150 },
    {
      field: "collection_type_id",
      headerName: "Collection Type",
      flex: 1,
      valueGetter: (params) => params.row.collection_type?.collection_type_name || "",
    },
    {
      field: "vendor_id",
      headerName: "Vendor",
      flex: 1,
      valueGetter: (params) => params.row.vendor?.vendor_name || "",
    },
    { field: "price", headerName: "Price", width: 100 },
    {
      field: "purchase_date",
      headerName: "Purchase Date",
      width: 130,
      valueGetter: (params) => {
        const val = params.row.purchase_date;
        if (!val) return "";
        if (val instanceof Date && !isNaN(val)) {
          return val.toLocaleDateString("en-GB");
        }
        if (typeof val === "string" && val.length >= 10) {
          const datePart = val.split("T")[0];
          const [y, m, d] = datePart.split("-");
          if (y && m && d) {
            if (!isNaN(Number(y)) && !isNaN(Number(m)) && !isNaN(Number(d))) {
              return `${d}/${m}/${y}`;
            }
          }
        }
        return "";
      },
    },
    { field: "shelf_location", headerName: "Shelf Location", flex: 1 },
    { field: "book_status", headerName: "Status", width: 120 },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <ActionsMenu
          row={params.row}
          onEdit={handleEditBookCopy}
          onDelete={handleDeleteBookCopy}
          onlyCopyActions={true}
        />
      ),
    },
  ];

  // Effect for server-side pagination
  useEffect(() => {
    if (clientId && collegeId && clientId !== "null" && collegeId !== "null") {
      fetchData(page, pageSize, search);
      fetchCategories();
      fetchPublishers();
    }
    // eslint-disable-next-line
  }, [clientId, collegeId, page, pageSize, search]);

  // Conditional return AFTER all hooks are declared
  if (idError) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">{idError}</Alert>
      </Box>
    );
  }

  return (
    <Box p={2}>
      <Paper elevation={1}>
        <Toolbar sx={{ gap: 1, flexWrap: "wrap" }}>
          <Typography variant="h5" sx={{ flexGrow: 1 }}>
            MANAGE BOOKS
          </Typography>
          <BookActionsMenu
            onAdd={handleOpen}
            onDownload={handleDownloadCsv}
            onUpload={handleUploadCsv}
          />
          <TextField
            size="medium"
            placeholder="Search books"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => fetchData(page, pageSize, search)}>
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 220, mr: 2 }}
          />
        </Toolbar>
        <Box height={400} mt={2} ml={2} mr={2}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            page={page}
            pageSize={pageSize}
            rowCount={rowCount}
            pagination
            paginationMode="server"
            onPageChange={(newPage) => setPage(newPage)}
            onPageSizeChange={(newPageSize) => {
              setPageSize(newPageSize);
              setPage(0);
            }}
            rowsPerPageOptions={[10, 20, 50]}
            disableSelectionOnClick
            autoHeight
            getRowId={(row) => row.book_id}
          />
        </Box>
      </Paper>

      {/** View Book Copies Dialog */}
      <Dialog
        open={viewCopiesOpen}
        onClose={() => setViewCopiesOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Book Copies - {viewCopiesBook?.title}</DialogTitle>
        <DialogContent>
          <Box sx={{ height: 400, mt: 2 }}>
            <DataGrid
              rows={Array.isArray(bookCopies) ? bookCopies : []}
              columns={bookCopiesColumns}
              loading={loadingCopies}
              getRowId={(row) => row.copy_id}
              autoHeight
              pageSize={10}
              rowsPerPageOptions={[10, 20, 50]}
              disableSelectionOnClick
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewCopiesOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Book Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? "Edit Book" : "Add Book"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Category"
                value={form.category_id}
                onChange={(e) =>
                  setForm({
                    ...form,
                    category_id: e.target.value === "" ? "" : Number(e.target.value),
                  })
                }
                select
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
                SelectProps={{ native: true, displayEmpty: true }}
              >
                <option key="empty-category" value="">
                  Select Category
                </option>
                {Array.isArray(categories) &&
                  categories.map((c) => (
                    <option key={c.category_id} value={c.category_id}>
                      {c.category_name}
                    </option>
                  ))}
              </TextField>
              <TextField
                label="Publisher"
                value={form.publisher_id}
                onChange={(e) =>
                  setForm({
                    ...form,
                    publisher_id: e.target.value === "" ? "" : Number(e.target.value),
                  })
                }
                select
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
                SelectProps={{ native: true, displayEmpty: true }}
              >
                <option key="empty-publisher" value="">
                  Select Publisher
                </option>
                {Array.isArray(publishers) &&
                  publishers.map((p) => (
                    <option key={p.publisher_id} value={p.publisher_id}>
                      {p.publisher_name}
                    </option>
                  ))}
              </TextField>
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Title"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                fullWidth
                required
              />
              <TextField
                label="Edition"
                value={form.edition}
                onChange={(e) => setForm({ ...form, edition: e.target.value })}
                fullWidth
              />
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="ISBN"
                value={form.isbn}
                onChange={(e) => setForm({ ...form, isbn: e.target.value })}
                fullWidth
              />
              <TextField
                label="Publication Year"
                value={form.publication_year}
                onChange={(e) => setForm({ ...form, publication_year: e.target.value })}
                fullWidth
              />
              <TextField
                label="Language"
                value={form.language}
                onChange={(e) => setForm({ ...form, language: e.target.value })}
                fullWidth
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" sx={{ color: "#fff" }}>
            {editing ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Book Copy Dialog */}
      <Dialog open={addCopyOpen} onClose={handleAddCopyClose} maxWidth="sm" fullWidth>
        <DialogTitle>Add Book Copy</DialogTitle>
        <DialogContent>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mb={2}>
            <TextField
              margin="dense"
              label="Collection Type"
              name="collection_type_id"
              value={addCopyForm.collection_type_id}
              onChange={handleAddCopyChange}
              select
              fullWidth
              InputLabelProps={{ shrink: true }}
              SelectProps={{ native: true, displayEmpty: true }}
            >
              <option key="empty-collection-type" value="">
                Select Collection Type
              </option>
              {Array.isArray(collectionTypes) &&
                collectionTypes.map((type) => (
                  <option key={type.collection_type_id} value={type.collection_type_id}>
                    {type.collection_type_name}
                  </option>
                ))}
            </TextField>
            <TextField
              margin="dense"
              label="Vendor"
              name="vendor_id"
              value={addCopyForm.vendor_id}
              onChange={handleAddCopyChange}
              select
              fullWidth
              InputLabelProps={{ shrink: true }}
              SelectProps={{ native: true, displayEmpty: true }}
            >
              <option key="empty-vendor" value="">
                Select Vendor
              </option>
              {Array.isArray(vendors) &&
                vendors.map((vendor) => (
                  <option key={vendor.vendor_id} value={vendor.vendor_id}>
                    {vendor.vendor_name}
                  </option>
                ))}
            </TextField>
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mb={2}>
            <TextField
              margin="dense"
              label="Accession No"
              name="accession_no"
              value={addCopyForm.accession_no}
              onChange={handleAddCopyChange}
              fullWidth
              required
            />
            <TextField
              margin="dense"
              label="Shelf Location"
              name="shelf_location"
              value={addCopyForm.shelf_location}
              onChange={handleAddCopyChange}
              fullWidth
            />
            <TextField
              margin="dense"
              label="Price"
              name="price"
              value={addCopyForm.price}
              onChange={handleAddCopyChange}
              type="number"
              fullWidth
            />
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              margin="dense"
              label="Purchase Date"
              name="purchase_date"
              value={addCopyForm.purchase_date}
              onChange={handleAddCopyChange}
              type="date"
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              margin="dense"
              label="Book Status"
              name="book_status"
              value={addCopyForm.book_status}
              onChange={handleAddCopyChange}
              select
              fullWidth
              InputLabelProps={{ shrink: true }}
              SelectProps={{ native: true, displayEmpty: true }}
            >
              <option key="empty-status" value="">
                Select Book Status
              </option>
              <option value="AVAILABLE">AVAILABLE</option>
              <option value="ISSUED">ISSUED</option>
              <option value="DAMAGED">DAMAGED</option>
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAddCopyClose}>Cancel</Button>
          <Button onClick={handleAddCopySave} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Book Copy Dialog */}
      <Dialog open={editCopyOpen} onClose={() => setEditCopyOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Book Copy</DialogTitle>
        <DialogContent>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mb={2}>
            <TextField
              margin="dense"
              label="Collection Type"
              name="collection_type_id"
              value={editCopyForm.collection_type_id || ""}
              onChange={handleEditCopyChange}
              select
              fullWidth
              InputLabelProps={{ shrink: true }}
              SelectProps={{ native: true, displayEmpty: true }}
            >
              <option key="empty-collection-type-edit" value="">
                Select Collection Type
              </option>
              {Array.isArray(collectionTypes) &&
                collectionTypes.map((type) => (
                  <option key={type.collection_type_id} value={type.collection_type_id}>
                    {type.collection_type_name}
                  </option>
                ))}
            </TextField>
            <TextField
              margin="dense"
              label="Vendor"
              name="vendor_id"
              value={editCopyForm.vendor_id || ""}
              onChange={handleEditCopyChange}
              select
              fullWidth
              InputLabelProps={{ shrink: true }}
              SelectProps={{ native: true, displayEmpty: true }}
            >
              <option key="empty-vendor-edit" value="">
                Select Vendor
              </option>
              {Array.isArray(vendors) &&
                vendors.map((vendor) => (
                  <option key={vendor.vendor_id} value={vendor.vendor_id}>
                    {vendor.vendor_name}
                  </option>
                ))}
            </TextField>
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mb={2}>
            <TextField
              margin="dense"
              label="Accession No"
              name="accession_no"
              value={editCopyForm.accession_no || ""}
              onChange={handleEditCopyChange}
              fullWidth
              required
            />
            <TextField
              margin="dense"
              label="Shelf Location"
              name="shelf_location"
              value={editCopyForm.shelf_location || ""}
              onChange={handleEditCopyChange}
              fullWidth
            />
            <TextField
              margin="dense"
              label="Price"
              name="price"
              value={editCopyForm.price || ""}
              onChange={handleEditCopyChange}
              type="number"
              fullWidth
            />
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              margin="dense"
              label="Purchase Date"
              name="purchase_date"
              value={editCopyForm.purchase_date || ""}
              onChange={handleEditCopyChange}
              type="date"
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              margin="dense"
              label="Book Status"
              name="book_status"
              value={editCopyForm.book_status || ""}
              onChange={handleEditCopyChange}
              select
              fullWidth
              InputLabelProps={{ shrink: true }}
              SelectProps={{ native: true, displayEmpty: true }}
            >
              <option key="empty-status-edit" value="">
                Select Book Status
              </option>
              <option value="AVAILABLE">AVAILABLE</option>
              <option value="ISSUED">ISSUED</option>
              <option value="DAMAGED">DAMAGED</option>
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditCopyOpen(false)}>Cancel</Button>
          <Button onClick={handleEditCopySave} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ width: "100%" }}
          elevation={6}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
